/**
 * Reglas de productos aplicables al carrito de un usuario NO logueado (task-1379).
 *
 * Para logueados el carrito del backend (useCart) ya devuelve el resumen de reglas
 * — descuento, envío gratis y qué reglas aplicaron — porque el carrito vive en la
 * base. Para no logueados el carrito vive solo en localStorage, así que nadie le
 * preguntaba al backend qué reglas aplican: el invitado veía el precio de lista.
 *
 * Eso importa porque el total que se cobra lo calcula el backend al crear la orden
 * (Webpay se inicia con el total que devuelve /orders). Sin este hook el invitado
 * vería un total y se le cobraría otro.
 *
 * Se consulta /product-rules/check con el correo cuando ya lo escribió en el
 * checkout: las reglas pueden estar segmentadas (top clientes, comuna, lista de
 * correos) y sin identificarlo el backend solo evalúa las de audiencia "todos" —
 * exactamente el mismo criterio que después usa la orden.
 *
 * También se manda la comuna de despacho: las reglas por comuna dependen de a
 * dónde va el pedido, y la orden las evalúa con la dirección real.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getDisplayPrice } from '../utils/priceCalculations';

const VACIO = {
  applied_product_rules: [],
  discount_from_rules: 0,
  free_shipping_from_rules: false,
  total_after_discount: 0,
  total: 0,
  subtotal: 0,
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Mismo criterio de precio que usan el sidecart, el checkout y el resumen de
// compra, para que el subtotal que se manda coincida con el que ve el cliente.
const subtotalLocal = (cart) =>
  (Array.isArray(cart) ? cart : []).reduce(
    (acc, item) => acc + num(getDisplayPrice(item)) * num(item.quantity),
    0
  );

// El backend identifica los productos por global_product_id, que en el carrito
// del front es `product_id`.
const productosParaReglas = (cart) =>
  (Array.isArray(cart) ? cart : [])
    .filter((item) => item && item.product_id)
    .map((item) => ({ id: Number(item.product_id), quantity: Math.max(1, num(item.quantity) || 1) }));

/**
 * @param {object}   opts
 * @param {Array}    opts.cart     Carrito local (state.cart).
 * @param {string?}  opts.email    Correo del cliente, si ya se conoce.
 * @param {number?}  opts.userId   Id del usuario, si lo hay.
 * @param {string?}  opts.comuna   Comuna de despacho, si ya se eligió dirección.
 * @param {boolean}  opts.enabled  false para logueados: el backend cart manda.
 * @returns {object|null} Resumen con la misma forma que cartSummary, o null.
 */
const useProductRules = ({ cart, email = null, userId = null, comuna = null, enabled = true }) => {
  const [resumen, setResumen] = useState(null);
  // Evita repetir la consulta cuando React vuelve a renderizar sin cambios reales.
  const ultimaClave = useRef(null);

  const productos = productosParaReglas(cart);
  const subtotal = subtotalLocal(cart);
  const clave = enabled ? JSON.stringify([productos, email || '', userId || '', comuna || '', Math.round(subtotal)]) : null;

  useEffect(() => {
    if (!enabled) {
      setResumen(null);
      ultimaClave.current = null;
      return undefined;
    }

    if (productos.length === 0) {
      setResumen(null);
      ultimaClave.current = null;
      return undefined;
    }

    if (ultimaClave.current === clave) {
      return undefined;
    }

    let cancelado = false;
    // Pequeño retardo: al cambiar cantidades el carrito se actualiza varias veces
    // seguidas y no tiene sentido consultar en cada tecla.
    const timer = setTimeout(() => {
      axios
        .get(`${process.env.BASE_URL}/product-rules/check`, {
          params: {
            products: JSON.stringify(productos),
            subtotal: Math.round(subtotal),
            ...(email ? { email } : {}),
            ...(userId ? { user_id: userId } : {}),
            ...(comuna ? { comuna } : {}),
          },
        })
        .then(({ data }) => {
          if (cancelado) return;
          ultimaClave.current = clave;

          const descuento = Math.max(0, Math.min(num(data?.discount_amount), subtotal));

          setResumen({
            applied_product_rules: Array.isArray(data?.applicable_rules) ? data.applicable_rules : [],
            discount_from_rules: descuento,
            free_shipping_from_rules: !!data?.free_shipping,
            total_after_discount: Math.max(0, subtotal - descuento),
            total: subtotal,
            subtotal,
          });
        })
        .catch(() => {
          if (cancelado) return;
          // Si la consulta falla se deja el carrito sin reglas: mostrar un descuento
          // que después el backend no aplique sería peor que no mostrarlo.
          ultimaClave.current = null;
          setResumen(null);
        });
    }, 300);

    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, enabled]);

  return resumen;
};

export { VACIO as RESUMEN_REGLAS_VACIO };
export default useProductRules;
